import { useMemo } from "react";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
} from "recharts";
import useAdminQuery from "../../lib/queries/useAdminQuery";
import {
  SEAT_HISTORY_BY_CCY_PLAN_SQL,
  SEAT_HISTORY_BY_COUNTRY_PLAN_SQL,
  SIGNUP_COHORT_SQL,
  INVOICED_FIRMS_SEATS_SQL,
  mapSeatHistoryRows,
  mapSeatHistoryByCountryPlanRows,
  mapSignupCohortRows,
  mapInvoicedFirmRows,
} from "./sql";
import type {
  ActiveUserByRoleRow,
  SeatHistoryRow,
  SeatHistoryByCountryPlanRow,
  SignupCohortRow,
  InvoicedFirmRow,
} from "./sql";
import { COLORS, RNAMES } from "./helpers";
import type { RegionKey } from "./sql";
import { ReferenceLine } from "recharts";

const STRIPE_DB = { database: "stripe" };

const CCY_TO_REGION: Record<string, RegionKey> = {
  NZD: "NZ",
  AUD: "AU",
  GBP: "UK",
};
function regionForCcy(ccy: string): RegionKey {
  return CCY_TO_REGION[ccy] ?? "Other";
}

const COUNTRY_TO_REGION: Record<string, RegionKey> = {
  NZ: "NZ",
  AU: "AU",
  GB: "UK",
  UK: "UK",
};
function regionForCountry(cc: string): RegionKey {
  return COUNTRY_TO_REGION[cc] ?? "Other";
}

const FORECAST_HORIZON_MONTHS = 3;
const CURVE_BUCKETS = 12; // months-since-signup we model in the conversion curve
const AAM_ROLES = new Set(["admin", "adviser", "manager"]);

const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
function formatYm(ym: string): string {
  const [y, m] = ym.split("-");
  const mi = m ? parseInt(m, 10) - 1 : 0;
  return `${MONTH_NAMES[mi]} ${y?.slice(2) ?? ""}`;
}

const PLANS = ["pro", "plus"] as const;
type Plan = (typeof PLANS)[number];

const PLAN_LABEL: Record<Plan, string> = { pro: "Pro", plus: "Plus" };
const PLAN_COLOR: Record<Plan, string> = {
  pro: "var(--primary-5)",
  plus: "var(--violet-5)",
};

// Used by the per-country line charts. Free has its own colour so it doesn't
// compete with Pro/Plus visually.
type PlanWithFree = "pro" | "plus" | "free";
const PLAN_FREE_LABEL: Record<PlanWithFree, string> = { pro: "Pro", plus: "Plus", free: "Free" };
const PLAN_FREE_COLOR: Record<PlanWithFree, string> = {
  pro: "var(--primary-5)",
  plus: "var(--violet-5)",
  free: "var(--fg-3)",
};

const ROLE_ORDER = ["admin", "adviser", "support", "manager"];
const ROLE_LABEL: Record<string, string> = {
  admin: "Admin",
  adviser: "Adviser",
  support: "Support",
  manager: "Manager",
};

const COUNTRY_LABEL: Record<string, string> = {
  NZ: "New Zealand",
  AU: "Australia",
  GB: "United Kingdom",
  UK: "United Kingdom",
  US: "United States",
  IE: "Ireland",
  ZA: "South Africa",
};

function fmt(n: number): string {
  return n.toLocaleString();
}

function pct(n: number, d: number, digits = 1): string {
  if (d === 0) return "—";
  return `${((n / d) * 100).toFixed(digits)}%`;
}

export function Detail({ rows }: { rows: ActiveUserByRoleRow[] }) {
  // Slim signup cohort (one row per active user/company) for the forecast math.
  const cohortResult = useAdminQuery(SIGNUP_COHORT_SQL);

  // Invoiced (non-Stripe) paying firms with their current AAM seat counts and
  // sub-start dates — overlaid onto each historical month so the time series
  // reflects the consolidated paid-seat base, not just Stripe.
  const invoicedFirmsResult = useAdminQuery(INVOICED_FIRMS_SEATS_SQL);

  // Per (month, country, plan-incl-free) AAM seat counts — app DB only.
  // Drives the per-country small-multiples line charts.
  const countryHistoryResult = useAdminQuery(SEAT_HISTORY_BY_COUNTRY_PLAN_SQL);

  // Historical paying-seat counts per (month, currency, plan) from Stripe.
  const historyResult = useAdminQuery(SEAT_HISTORY_BY_CCY_PLAN_SQL, [], STRIPE_DB);
  const historyData = useMemo(() => {
    type Point = { ym: string; label: string; NZ: number; AU: number; UK: number; Other: number };
    const empty = (ym: string): Point => ({
      ym, label: formatYm(ym), NZ: 0, AU: 0, UK: 0, Other: 0,
    });
    if (!historyResult.data) {
      return { pro: [], plus: [], months: [] as string[], lastActualLabel: "" };
    }
    const histRows: SeatHistoryRow[] = mapSeatHistoryRows(
      historyResult.data.columns,
      historyResult.data.rows as unknown[][],
    );

    // 1. Historical Stripe-billed seats per (month, plan, region).
    const proByMonth = new Map<string, Point>();
    const plusByMonth = new Map<string, Point>();
    const monthsSet = new Set<string>();
    for (const r of histRows) {
      monthsSet.add(r.ym);
      const region = regionForCcy(r.ccy);
      const bucket = r.plan === "pro" ? proByMonth : r.plan === "plus" ? plusByMonth : null;
      if (!bucket) continue;
      if (!bucket.has(r.ym)) bucket.set(r.ym, empty(r.ym));
      bucket.get(r.ym)![region] += r.seats;
    }
    const histMonths = Array.from(monthsSet).sort();
    for (const ym of histMonths) {
      if (!proByMonth.has(ym)) proByMonth.set(ym, empty(ym));
      if (!plusByMonth.has(ym)) plusByMonth.set(ym, empty(ym));
    }

    // 1b. Overlay invoiced (non-Stripe) firms. Each invoiced firm contributes
    // its current AAM seat count to every month from its subscription start
    // date onward. This brings the time series in line with the consolidated
    // paid-seat base — Stripe alone misses invoiced customers (heaviest in NZ).
    if (invoicedFirmsResult.data) {
      const invoicedFirms: InvoicedFirmRow[] = mapInvoicedFirmRows(
        invoicedFirmsResult.data.columns,
        invoicedFirmsResult.data.rows as unknown[][],
      );
      for (const firm of invoicedFirms) {
        if (firm.aam_seats <= 0) continue;
        const region = regionForCountry(firm.country_code);
        const startTime = firm.start_date ? Date.parse(firm.start_date) : NaN;
        const startYm = isFinite(startTime)
          ? `${new Date(startTime).getUTCFullYear()}-${String(
              new Date(startTime).getUTCMonth() + 1,
            ).padStart(2, "0")}`
          : null;
        const bucket = firm.plan.toLowerCase() === "pro" ? proByMonth : plusByMonth;
        for (const ym of histMonths) {
          if (startYm && ym < startYm) continue;
          if (!bucket.has(ym)) bucket.set(ym, empty(ym));
          bucket.get(ym)![region] += firm.aam_seats;
        }
      }
    }

    const sortByYm = (a: Point, b: Point) => a.ym.localeCompare(b.ym);
    const proHist = Array.from(proByMonth.values()).sort(sortByYm);
    const plusHist = Array.from(plusByMonth.values()).sort(sortByYm);

    // 2. Forecast extension. Skipped if we don't have the cohort yet — the
    // history alone still renders.
    if (!cohortResult.data) {
      return { pro: proHist, plus: plusHist, months: histMonths, lastActualLabel: formatYm(histMonths[histMonths.length - 1] ?? "") };
    }
    const cohort: SignupCohortRow[] = mapSignupCohortRows(
      cohortResult.data.columns,
      cohortResult.data.rows as unknown[][],
    );

    // Build the conversion curve from signups 12+ months old so we observe a
    // full window. cumulative[m] = share of those signups who had converted
    // (paid-plan started after signup) within m months of signing up.
    const now = new Date();
    const MS_PER_DAY = 24 * 60 * 60 * 1000;
    const DAYS_PER_MONTH = 30.4375;
    const matureCutoff = now.getTime() - 12 * 30 * MS_PER_DAY;
    let matureCount = 0;
    const matureConvByMonth = new Array(CURVE_BUCKETS + 1).fill(0);
    for (const row of cohort) {
      const t = Date.parse(row.signup_date);
      if (!isFinite(t) || t > matureCutoff) continue;
      matureCount++;
      if (!row.converted_date) continue;
      const c = Date.parse(row.converted_date);
      if (!isFinite(c) || c <= t) continue;
      const months = Math.ceil((c - t) / MS_PER_DAY / DAYS_PER_MONTH);
      for (let m = Math.min(months, CURVE_BUCKETS); m <= CURVE_BUCKETS; m++) {
        matureConvByMonth[m]++;
      }
    }
    const cumulativeRate = matureConvByMonth.map((c) =>
      matureCount > 0 ? c / matureCount : 0,
    );

    // Pro/Plus mix among historical converters (global; small sample sizes per
    // region make per-region mix noisy on dev data — fall back if needed).
    let mixPro = 0, mixPlus = 0;
    for (const row of cohort) {
      if (!row.converted_date || !row.billing_plan) continue;
      const p = row.billing_plan.toLowerCase();
      if (p === "pro") mixPro++;
      else if (p === "plus") mixPlus++;
    }
    const mixTotal = mixPro + mixPlus;
    const proShare = mixTotal > 0 ? mixPro / mixTotal : 0.4;
    const plusShare = mixTotal > 0 ? mixPlus / mixTotal : 0.6;

    // Linear interpolation of the cumulative conversion rate at fractional
    // months-since-signup (curve is sampled at integer months).
    const rateAt = (t: number): number => {
      if (t <= 0) return cumulativeRate[0] ?? 0;
      if (t >= CURVE_BUCKETS) return cumulativeRate[CURVE_BUCKETS] ?? 0;
      const lo = Math.floor(t);
      const hi = lo + 1;
      const frac = t - lo;
      return (cumulativeRate[lo] ?? 0) * (1 - frac) + (cumulativeRate[hi] ?? 0) * frac;
    };

    // Forecast horizons: end of current month, end of next, end of the month
    // after that. Pinned to calendar month-end so the chart's first forecast
    // point sits at end-of-this-month rather than next month.
    const forecastTargets: Array<{ ym: string; monthsAhead: number }> = [];
    for (let f = 0; f < FORECAST_HORIZON_MONTHS; f++) {
      const monthEnd = new Date(now.getFullYear(), now.getMonth() + f + 1, 0);
      const ym = `${monthEnd.getFullYear()}-${String(monthEnd.getMonth() + 1).padStart(2, "0")}`;
      const daysAhead = (monthEnd.getTime() - now.getTime()) / MS_PER_DAY;
      const monthsAhead = Math.max(0, daysAhead / DAYS_PER_MONTH);
      forecastTargets.push({ ym, monthsAhead });
    }

    // For each unconverted active user, accumulate cumulative expected
    // conversions for each forecast month-end, attributed to their region.
    type RegionSeats = Record<RegionKey, number>;
    const emptyRegionSeats = (): RegionSeats => ({ NZ: 0, AU: 0, UK: 0, Other: 0 });
    const cumPerTarget: RegionSeats[] = forecastTargets.map(() => emptyRegionSeats());
    for (const row of cohort) {
      if (row.on_paid_plan) continue;
      const t = Date.parse(row.signup_date);
      if (!isFinite(t)) continue;
      const monthsSince = Math.max(0, (now.getTime() - t) / MS_PER_DAY / DAYS_PER_MONTH);
      const rateAtNow = rateAt(monthsSince);
      if (rateAtNow >= 1) continue;
      const region = regionForCountry(row.country_code);
      for (let f = 0; f < forecastTargets.length; f++) {
        const rateAtFuture = rateAt(monthsSince + forecastTargets[f]!.monthsAhead);
        const cumProb = Math.max(0, (rateAtFuture - rateAtNow) / (1 - rateAtNow));
        cumPerTarget[f]![region] += cumProb;
      }
    }

    // Split history off so the current-month snapshot is replaced by the
    // end-of-current-month forecast below. Boundary marker sits at the last
    // closed month (one before the current calendar month).
    const currentYm = forecastTargets[0]!.ym;
    const proClosed = proHist.filter((p) => p.ym < currentYm);
    const plusClosed = plusHist.filter((p) => p.ym < currentYm);
    const currentSnapshotPro = proHist.find((p) => p.ym === currentYm) ?? empty(currentYm);
    const currentSnapshotPlus = plusHist.find((p) => p.ym === currentYm) ?? empty(currentYm);
    const lastClosedYm = proClosed[proClosed.length - 1]?.ym ?? "";

    const proForecast: Point[] = forecastTargets.map((t, f) => {
      const cum = cumPerTarget[f]!;
      return {
        ym: t.ym,
        label: formatYm(t.ym),
        NZ: Math.round(currentSnapshotPro.NZ + cum.NZ * proShare),
        AU: Math.round(currentSnapshotPro.AU + cum.AU * proShare),
        UK: Math.round(currentSnapshotPro.UK + cum.UK * proShare),
        Other: Math.round(currentSnapshotPro.Other + cum.Other * proShare),
      };
    });
    const plusForecast: Point[] = forecastTargets.map((t, f) => {
      const cum = cumPerTarget[f]!;
      return {
        ym: t.ym,
        label: formatYm(t.ym),
        NZ: Math.round(currentSnapshotPlus.NZ + cum.NZ * plusShare),
        AU: Math.round(currentSnapshotPlus.AU + cum.AU * plusShare),
        UK: Math.round(currentSnapshotPlus.UK + cum.UK * plusShare),
        Other: Math.round(currentSnapshotPlus.Other + cum.Other * plusShare),
      };
    });

    return {
      pro: [...proClosed, ...proForecast],
      plus: [...plusClosed, ...plusForecast],
      months: [...proClosed.map((p) => p.ym), ...proForecast.map((p) => p.ym)],
      lastActualLabel: lastClosedYm ? formatYm(lastClosedYm) : "",
    };
  }, [historyResult.data, cohortResult.data, invoicedFirmsResult.data]);


  // Paying AAM seats only — exclude free, support roles, and inactive seats.
  // SQL already filters status='active'; this enforces role + plan.
  const payingRows = useMemo(
    () =>
      rows.filter((r) => {
        const p = r.billing_plan.toLowerCase();
        const role = r.role.toLowerCase();
        return (p === "pro" || p === "plus") && AAM_ROLES.has(role);
      }),
    [rows],
  );

  const totals = useMemo(() => {
    let pro = 0;
    let plus = 0;
    for (const r of payingRows) {
      const c = r.seat_count;
      const p = r.billing_plan.toLowerCase();
      if (p === "pro") pro += c;
      else if (p === "plus") plus += c;
    }
    return { pro, plus, paying: pro + plus };
  }, [payingRows]);

  const countries = useMemo(() => {
    const totalsByCountry: Record<string, number> = {};
    for (const r of payingRows) {
      totalsByCountry[r.country_code] = (totalsByCountry[r.country_code] ?? 0) + r.seat_count;
    }
    return Object.keys(totalsByCountry).sort(
      (a, b) => (totalsByCountry[b] ?? 0) - (totalsByCountry[a] ?? 0),
    );
  }, [payingRows]);

  const planByCountry = useMemo(() => {
    const m: Record<Plan, Record<string, number>> = { pro: {}, plus: {} };
    for (const r of payingRows) {
      const p = r.billing_plan.toLowerCase() as Plan;
      if (!PLANS.includes(p)) continue;
      m[p][r.country_code] = (m[p][r.country_code] ?? 0) + r.seat_count;
    }
    return m;
  }, [payingRows]);

  const planTotals: Record<Plan, number> = {
    pro: totals.pro,
    plus: totals.plus,
  };

  const roles = useMemo(() => {
    const set = new Set<string>();
    payingRows.forEach((r) => set.add(r.role));
    const arr = Array.from(set);
    arr.sort((a, b) => {
      const ai = ROLE_ORDER.indexOf(a);
      const bi = ROLE_ORDER.indexOf(b);
      return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
    });
    return arr;
  }, [payingRows]);

  const roleByPlan = useMemo(() => {
    const m: Record<string, Record<Plan, number>> = {};
    for (const role of roles) m[role] = { pro: 0, plus: 0 };
    for (const r of payingRows) {
      const p = r.billing_plan.toLowerCase() as Plan;
      if (!PLANS.includes(p)) continue;
      if (!m[r.role]) m[r.role] = { pro: 0, plus: 0 };
      m[r.role]![p] += r.seat_count;
    }
    return m;
  }, [payingRows, roles]);

  // Per-region time-series for the small-multiples line charts.
  // Country rows from the SQL are folded into NZ / AU / UK / Other regions
  // via regionForCountry so the chart matches the rest of the dashboard.
  type RegionSeriesPoint = { ym: string; label: string; pro: number; plus: number; free: number };
  const regionSeriesByRegion = useMemo(() => {
    const empty: Record<RegionKey, Map<string, RegionSeriesPoint>> = {
      NZ: new Map(), AU: new Map(), UK: new Map(), Other: new Map(),
    };
    if (!countryHistoryResult.data) return { byRegion: empty, months: [] as string[] };
    const rows: SeatHistoryByCountryPlanRow[] = mapSeatHistoryByCountryPlanRows(
      countryHistoryResult.data.columns,
      countryHistoryResult.data.rows as unknown[][],
    );
    const monthsSet = new Set<string>();
    for (const r of rows) {
      monthsSet.add(r.ym);
      const region = regionForCountry(r.country_code);
      const ymMap = empty[region];
      if (!ymMap.has(r.ym)) ymMap.set(r.ym, { ym: r.ym, label: formatYm(r.ym), pro: 0, plus: 0, free: 0 });
      ymMap.get(r.ym)![r.plan] += r.seats;
    }
    return { byRegion: empty, months: Array.from(monthsSet).sort() };
  }, [countryHistoryResult.data]);

  // Region list for the line-chart grid — fixed order (NZ, AU, UK, Other) so
  // it lines up visually with the rest of the dashboard. A region with no
  // seats at the latest month is hidden.
  const regionsForLines = useMemo(() => {
    const order: RegionKey[] = ["NZ", "AU", "UK", "Other"];
    const months = regionSeriesByRegion.months;
    const lastYm = months[months.length - 1];
    return order.filter((r) => {
      if (!lastYm) return false;
      const point = regionSeriesByRegion.byRegion[r].get(lastYm);
      return point != null && (point.pro + point.plus + point.free) > 0;
    });
  }, [regionSeriesByRegion]);

  // Per-region materialized series (months filled with zeros where missing).
  const seriesForRegion = useMemo(() => {
    const months = regionSeriesByRegion.months;
    const out: Record<RegionKey, RegionSeriesPoint[]> = { NZ: [], AU: [], UK: [], Other: [] };
    for (const r of ["NZ", "AU", "UK", "Other"] as RegionKey[]) {
      const ymMap = regionSeriesByRegion.byRegion[r];
      out[r] = months.map((ym) =>
        ymMap.get(ym) ?? { ym, label: formatYm(ym), pro: 0, plus: 0, free: 0 },
      );
    }
    return out;
  }, [regionSeriesByRegion]);

  return (
    <>
      <div className="page-header" style={{ marginTop: 4 }}>
        <div>
          <h1 style={{ fontSize: 22 }}>Paying seats, detailed view</h1>
          <p className="lede">
            Paying-seat distribution (Pro and Plus, AAM roles only — admin /
            adviser / manager) across countries. Source: app DB, joined across
            user_roles (status='active'), company_billing, and companies. Free
            seats and support-role seats excluded. One row in the source equals
            one seat.
          </p>
        </div>
      </div>

      {/* KPI row */}
      <div className="kpi-grid" style={{ gridTemplateColumns: "repeat(3, 1fr)" }}>
        <div className="kpi">
          <div className="kpi-label">Paying seats</div>
          <div className="kpi-value" style={{ fontFamily: "var(--font-mono)" }}>
            {fmt(totals.paying)}
          </div>
          <div className="kpi-sub">
            <span className="badge b-gray">{countries.length} countries · {roles.length} roles</span>
          </div>
        </div>
        <div className="kpi">
          <div className="kpi-label">Pro seats</div>
          <div className="kpi-value" style={{ fontFamily: "var(--font-mono)" }}>
            {fmt(totals.pro)}
          </div>
          <div className="kpi-sub">
            <span className="badge b-blue">{pct(totals.pro, totals.paying)} of paying</span>
          </div>
        </div>
        <div className="kpi">
          <div className="kpi-label">Plus seats</div>
          <div className="kpi-value" style={{ fontFamily: "var(--font-mono)" }}>
            {fmt(totals.plus)}
          </div>
          <div className="kpi-sub">
            <span className="badge b-violet">{pct(totals.plus, totals.paying)} of paying</span>
          </div>
        </div>
      </div>

      {/* Stacked time series: paying seats over time, one chart per plan.
          Forecast horizon = 3 months, shaded after the boundary marker. */}
      <div className="grid-2" style={{ marginBottom: 16 }}>
        <SeatTimeSeriesCard
          title="Pro seats over time"
          data={historyData.pro}
          lastActualLabel={historyData.lastActualLabel}
          gradientKey="pro"
        />
        <SeatTimeSeriesCard
          title="Plus seats over time"
          data={historyData.plus}
          lastActualLabel={historyData.lastActualLabel}
          gradientKey="plus"
        />
      </div>

      {/* Plan composition by region — small-multiples line charts.
          One mini chart per region (NZ / AU / UK / ROW), three lines per chart
          (Pro / Plus / Free). AAM (admin/adviser/manager) seats only; Free
          counts proxy via users.created_at_utc since user_roles has no own
          timestamp. */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-h" style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "flex-start" }}>
          <h2>Plan composition by region</h2>
          <span className="sub">
            AAM seats over time per region (NZ, AU, UK, ROW), split by plan (Pro, Plus, Free).
          </span>
        </div>
        <div className="card-body">
          {countryHistoryResult.isLoading ? (
            <div className="empty">Loading…</div>
          ) : regionsForLines.length === 0 ? (
            <div className="empty">No region history available.</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {regionsForLines.map((r) => (
                <RegionPlanLineChart
                  key={r}
                  region={r}
                  data={seriesForRegion[r]}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Two side-by-side matrix tables */}
      <div className="grid-2" style={{ marginBottom: 16 }}>
        {/* Plan x Country */}
        <div className="card" style={{ marginBottom: 0 }}>
          <div className="card-h" style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "flex-start" }}>
            <h2>Plan x country</h2>
            <span className="sub">Paying-seat counts. Rows are plans, columns are ISO country codes.</span>
          </div>
          <div className="table-wrap no-border-top">
            <table className="t">
              <thead>
                <tr>
                  <th>Plan</th>
                  {countries.map((cc) => (
                    <th key={cc} className="num">{cc}</th>
                  ))}
                  <th className="num">Total</th>
                </tr>
              </thead>
              <tbody>
                {PLANS.map((p) => {
                  const rowTotal = planTotals[p];
                  return (
                    <tr key={p}>
                      <td>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <div style={{ width: 8, height: 8, borderRadius: 2, background: PLAN_COLOR[p] }} />
                          <span style={{ fontWeight: 600 }}>{PLAN_LABEL[p]}</span>
                        </div>
                      </td>
                      {countries.map((cc) => {
                        const v = planByCountry[p][cc] ?? 0;
                        return (
                          <td key={cc} className="num mono" style={{ color: v === 0 ? "var(--fg-3)" : "var(--fg-1)" }}>
                            {v === 0 ? "—" : fmt(v)}
                          </td>
                        );
                      })}
                      <td className="num mono" style={{ fontWeight: 700 }}>{fmt(rowTotal)}</td>
                    </tr>
                  );
                })}
                <tr>
                  <td style={{ fontWeight: 700, color: "var(--fg-2)" }}>Total</td>
                  {countries.map((cc) => {
                    const v =
                      (planByCountry.pro[cc] ?? 0) +
                      (planByCountry.plus[cc] ?? 0);
                    return (
                      <td key={cc} className="num mono" style={{ fontWeight: 700 }}>
                        {fmt(v)}
                      </td>
                    );
                  })}
                  <td className="num mono" style={{ fontWeight: 700 }}>{fmt(totals.paying)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Role x Plan */}
        <div className="card" style={{ marginBottom: 0 }}>
          <div className="card-h" style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "flex-start" }}>
            <h2>Role x plan</h2>
            <span className="sub">Paying seats per role, split between Pro and Plus.</span>
          </div>
          <div className="table-wrap no-border-top">
            <table className="t">
              <thead>
                <tr>
                  <th>Role</th>
                  <th className="num">Pro</th>
                  <th className="num">Plus</th>
                  <th className="num">Total</th>
                </tr>
              </thead>
              <tbody>
                {roles.map((role) => {
                  const r = roleByPlan[role] ?? { pro: 0, plus: 0 };
                  const total = r.pro + r.plus;
                  return (
                    <tr key={role}>
                      <td style={{ fontWeight: 600 }}>{ROLE_LABEL[role] ?? role}</td>
                      <td className="num mono" style={{ color: r.pro === 0 ? "var(--fg-3)" : "var(--fg-1)" }}>
                        {r.pro === 0 ? "—" : fmt(r.pro)}
                      </td>
                      <td className="num mono" style={{ color: r.plus === 0 ? "var(--fg-3)" : "var(--fg-1)" }}>
                        {r.plus === 0 ? "—" : fmt(r.plus)}
                      </td>
                      <td className="num mono" style={{ fontWeight: 700 }}>{fmt(total)}</td>
                    </tr>
                  );
                })}
                <tr>
                  <td style={{ fontWeight: 700, color: "var(--fg-2)" }}>Total</td>
                  <td className="num mono" style={{ fontWeight: 700 }}>{fmt(totals.pro)}</td>
                  <td className="num mono" style={{ fontWeight: 700 }}>{fmt(totals.plus)}</td>
                  <td className="num mono" style={{ fontWeight: 700 }}>{fmt(totals.paying)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Raw breakdown table */}
      <div className="card">
        <div className="card-h" style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "flex-start" }}>
          <h2>Full breakdown</h2>
          <span className="sub">
            Every (role, plan, country) combination with at least one paying seat. Sorted by seat count.
          </span>
        </div>
        <div className="table-wrap no-border-top">
          <table className="t">
            <thead>
              <tr>
                <th>Role</th>
                <th>Plan</th>
                <th>Country</th>
                <th className="num">Seats</th>
                <th className="num">% of paying</th>
              </tr>
            </thead>
            <tbody>
              {[...payingRows]
                .sort((a, b) => b.seat_count - a.seat_count)
                .map((r, i) => {
                  const plan = r.billing_plan.toLowerCase() as Plan;
                  const planColor = PLANS.includes(plan) ? PLAN_COLOR[plan] : "var(--fg-3)";
                  return (
                    <tr key={`${r.role}-${r.billing_plan}-${r.country_code}-${i}`}>
                      <td style={{ fontWeight: 600 }}>{ROLE_LABEL[r.role] ?? r.role}</td>
                      <td>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <div style={{ width: 6, height: 6, borderRadius: 2, background: planColor }} />
                          <span>{PLAN_LABEL[plan] ?? r.billing_plan}</span>
                        </div>
                      </td>
                      <td>
                        <span style={{ fontFamily: "var(--font-mono)", fontSize: 12, marginRight: 6 }}>
                          {r.country_code}
                        </span>
                        <span style={{ color: "var(--fg-3)", fontSize: 12 }}>
                          {COUNTRY_LABEL[r.country_code] ?? ""}
                        </span>
                      </td>
                      <td className="num mono" style={{ fontWeight: 700 }}>{fmt(r.seat_count)}</td>
                      <td className="num mono" style={{ color: "var(--fg-3)" }}>
                        {pct(r.seat_count, totals.paying)}
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="callout" style={{ marginTop: 16, lineHeight: 1.7 }}>
        <i className="ti ti-info-circle" />
        <div>
          <strong>Notes.</strong> Paying seats only (Pro + Plus, adviser / admin /
          manager — support excluded). The time series combines Stripe-billed
          history (subscription_cycle / _create / _update line quantities) with
          invoiced (non-Stripe) firms from app DB, each invoiced firm contributing
          its current AAM seat count from its subscription_started_date_utc onward.
          KPIs and matrices below come from the app DB only.
        </div>
      </div>
    </>
  );
}

const TIME_SERIES_REGIONS: RegionKey[] = ["NZ", "AU", "UK", "Other"];

type SeatTooltipPayloadEntry = { name?: string; value?: number; color?: string };

function SeatTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: SeatTooltipPayloadEntry[];
  label?: string;
}) {
  if (!active || !payload || payload.length === 0) return null;
  const total = payload.reduce((s, p) => s + Number(p.value ?? 0), 0);
  return (
    <div
      style={{
        background: "var(--white)",
        border: "1px solid var(--border-default)",
        borderRadius: 6,
        padding: "8px 10px",
        boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
        fontSize: 12,
        minWidth: 160,
      }}
    >
      <div style={{ fontWeight: 700, marginBottom: 6 }}>{label}</div>
      {payload.map((p, i) => (
        <div
          key={i}
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: 16,
            alignItems: "center",
            marginBottom: 2,
          }}
        >
          <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: 2,
                background: p.color,
                display: "inline-block",
              }}
            />
            <span style={{ color: "var(--fg-2)" }}>{p.name}</span>
          </span>
          <span style={{ fontFamily: "var(--font-mono)", fontWeight: 600 }}>
            {fmt(Number(p.value ?? 0))}
          </span>
        </div>
      ))}
      <div
        style={{
          marginTop: 6,
          paddingTop: 6,
          borderTop: "1px solid var(--border-default)",
          display: "flex",
          justifyContent: "space-between",
          gap: 16,
          fontWeight: 700,
        }}
      >
        <span>Total</span>
        <span style={{ fontFamily: "var(--font-mono)" }}>{fmt(total)}</span>
      </div>
    </div>
  );
}

function SeatTimeSeriesCard({
  title,
  data,
  lastActualLabel,
  gradientKey,
}: {
  title: string;
  data: Array<{ ym: string; label: string; NZ: number; AU: number; UK: number; Other: number }>;
  lastActualLabel: string;
  gradientKey: string;
}) {
  const empty = data.length === 0;
  // The gradient stops are mapped in objectBoundingBox space (0..1) across each
  // area's filled shape. Continuous data ⇒ the shape spans the whole data
  // range, so (boundaryIndex + 0.5) / (n - 1) maps the hard-edge stop to the
  // visual midpoint between the last actual and the first forecast month.
  const boundaryIndex = data.findIndex((d) => d.label === lastActualLabel);
  const boundaryFrac = boundaryIndex >= 0 && data.length > 1
    ? (boundaryIndex + 0.5) / (data.length - 1)
    : 1;
  const ACTUAL_OPACITY = 0.7;
  const FORECAST_OPACITY = 0.22;
  return (
    <div className="card" style={{ marginBottom: 0 }}>
      <div className="card-h" style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "flex-start" }}>
        <h2>{title}</h2>
        <span className="sub">
          End-of-month billed seats stacked by region. The lighter, translucent
          area to the right of the dashed line is the forecast: each
          currently-unconverted signup's expected conversion probability
          applied to the next 3 calendar month-ends, weighted by historical
          Pro/Plus mix and home country.
        </span>
      </div>
      <div className="card-body">
        <ResponsiveContainer width="100%" height={260}>
          <AreaChart data={data} margin={{ top: 26, right: 12, left: 0, bottom: 4 }}>
            <defs>
              {TIME_SERIES_REGIONS.map((r) => (
                <linearGradient
                  key={r}
                  id={`mrr-seat-gr-${gradientKey}-${r}`}
                  x1="0"
                  y1="0"
                  x2="1"
                  y2="0"
                >
                  <stop offset="0" stopColor={COLORS[r]} stopOpacity={ACTUAL_OPACITY} />
                  <stop
                    offset={Math.max(0, boundaryFrac - 0.001)}
                    stopColor={COLORS[r]}
                    stopOpacity={ACTUAL_OPACITY}
                  />
                  <stop offset={boundaryFrac} stopColor={COLORS[r]} stopOpacity={FORECAST_OPACITY} />
                  <stop offset="1" stopColor={COLORS[r]} stopOpacity={FORECAST_OPACITY} />
                </linearGradient>
              ))}
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 10, fill: "var(--fg-3)" }} interval={0} />
            <YAxis tick={{ fontSize: 10, fill: "var(--fg-3)" }} width={36} allowDecimals={false} />
            <Tooltip cursor={{ fill: "rgba(0,0,0,0.03)" }} content={<SeatTooltip />} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            {TIME_SERIES_REGIONS.map((r) => (
              <Area
                key={r}
                type="monotone"
                dataKey={r}
                name={r === "Other" ? "International" : (RNAMES[r] ?? r)}
                stackId="seats"
                stroke={COLORS[r]}
                strokeOpacity={0.85}
                fill={`url(#mrr-seat-gr-${gradientKey}-${r})`}
                fillOpacity={1}
                isAnimationActive={false}
              />
            ))}
            {lastActualLabel && (
              <ReferenceLine
                x={lastActualLabel}
                stroke="var(--fg-2)"
                strokeDasharray="4 4"
                label={{
                  value: "Forecast →",
                  position: "insideTopRight",
                  fontSize: 11,
                  fill: "var(--fg-2)",
                  offset: 8,
                }}
              />
            )}
          </AreaChart>
        </ResponsiveContainer>
        {empty && (
          <div style={{ textAlign: "center", color: "var(--fg-3)", fontSize: 12, marginTop: 8 }}>
            No history yet.
          </div>
        )}
      </div>
    </div>
  );
}

// Small line chart for a single region (NZ / AU / UK / Other), three lines:
// Pro / Plus / Free. Renders inside a grid of small multiples on the Detail
// tab. Region naming and colours match the rest of the MRR dashboard so the
// chart aligns with KPI tiles and stacked time series.
const REGION_DISPLAY: Record<RegionKey, string> = {
  NZ: "New Zealand",
  AU: "Australia",
  UK: "United Kingdom",
  Other: "Rest of world",
};
const REGION_CODE: Record<RegionKey, string> = {
  NZ: "NZ", AU: "AU", UK: "UK", Other: "ROW",
};
function RegionPlanLineChart({
  region,
  data,
}: {
  region: RegionKey;
  data: { ym: string; label: string; pro: number; plus: number; free: number }[];
}) {
  const latest = data[data.length - 1];
  const total = latest ? latest.pro + latest.plus + latest.free : 0;
  return (
    <div
      style={{
        border: "1px solid var(--border-default)",
        borderRadius: 6,
        padding: "10px 12px",
        background: "var(--white)",
      }}
    >
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12, marginBottom: 6 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ width: 8, height: 8, borderRadius: "50%", background: COLORS[region], display: "inline-block" }} />
          <span style={{ fontSize: 12, fontWeight: 600, color: "var(--fg-2)" }}>{REGION_CODE[region]}</span>
          <span style={{ fontSize: 11, color: "var(--fg-3)" }}>{REGION_DISPLAY[region]}</span>
          <span style={{ fontSize: 11, color: "var(--fg-3)", fontFamily: "var(--font-mono)" }}>
            · {fmt(total)} total
          </span>
        </div>
        <div style={{ display: "flex", alignItems: "baseline", gap: 14, flexWrap: "wrap", justifyContent: "flex-end" }}>
          {(["pro", "plus", "free"] as PlanWithFree[]).map((p) => (
            <div key={p} style={{ display: "flex", alignItems: "baseline", gap: 5 }}>
              <span
                style={{
                  display: "inline-block",
                  width: 10,
                  height: 2,
                  background: PLAN_FREE_COLOR[p],
                  borderTop: p === "free" ? "1px dashed var(--fg-3)" : "none",
                  alignSelf: "center",
                }}
              />
              <span style={{ fontSize: 11, color: "var(--fg-3)" }}>{PLAN_FREE_LABEL[p]}</span>
              <span style={{ fontSize: 13, fontWeight: 700, color: "var(--fg-1)", fontFamily: "var(--font-mono)" }}>
                {fmt(latest?.[p] ?? 0)}
              </span>
            </div>
          ))}
        </div>
      </div>
      <ResponsiveContainer width="100%" height={200}>
        <LineChart data={data} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" vertical={false} />
          <XAxis
            dataKey="label"
            tick={{ fontSize: 10, fill: "var(--fg-3)" }}
            interval="preserveStartEnd"
            minTickGap={20}
          />
          {/* Left axis: Pro + Plus (paid seats). Right axis: Free (typically
              an order of magnitude larger, so a shared axis flattens the
              paid lines visually). */}
          <YAxis
            yAxisId="paid"
            tick={{ fontSize: 10, fill: "var(--fg-3)" }}
            allowDecimals={false}
            width={36}
          />
          <YAxis
            yAxisId="free"
            orientation="right"
            tick={{ fontSize: 10, fill: "var(--fg-3)" }}
            allowDecimals={false}
            width={40}
            label={{
              value: "Free",
              position: "insideTopRight",
              fontSize: 10,
              fill: "var(--fg-3)",
              offset: 6,
            }}
          />
          <Tooltip
            cursor={{ stroke: "rgba(0,0,0,0.1)" }}
            formatter={(value, name) => [fmt(Number(value ?? 0)), String(name)]}
            contentStyle={{ fontSize: 11 }}
          />
          <Line
            yAxisId="paid"
            type="monotone"
            dataKey="pro"
            name={PLAN_FREE_LABEL.pro}
            stroke={PLAN_FREE_COLOR.pro}
            strokeWidth={2}
            dot={false}
            isAnimationActive={false}
          />
          <Line
            yAxisId="paid"
            type="monotone"
            dataKey="plus"
            name={PLAN_FREE_LABEL.plus}
            stroke={PLAN_FREE_COLOR.plus}
            strokeWidth={2}
            dot={false}
            isAnimationActive={false}
          />
          <Line
            yAxisId="free"
            type="monotone"
            dataKey="free"
            name={PLAN_FREE_LABEL.free}
            stroke={PLAN_FREE_COLOR.free}
            strokeWidth={1.5}
            strokeDasharray="4 3"
            dot={false}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
